import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import useAuth from '../auth/useAuth';
import { QueryKeys, SURVEY_ID_HEADER } from '../constants';
import { API_BASE_URL, MissingIdError, useFetch } from './utils';

export function useUpdateDisplaySentiment() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, surveyId, data }) => {
      if (!surveyId) {
        throw new MissingIdError('surveyId');
      }
      return fetcher({
        path: `v1/questions/${questionId}/update-display-sentiment/`,
        method: 'PATCH',
        data,
      });
    },
    onSettled: (_data, _error, { questionId, surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.QUESTIONS, { surveyId }]);
      queryClient.invalidateQueries([QueryKeys.QUESTION, { questionId, surveyId }]);
    },
    onError: () => {
      message.error('Something went wrong updating sentiment display');
    },
  });
}

// not using useFetch here because we need send the body as a blob
// TODO: convert to custom hook
export function useRawExport() {
  const { getAccessTokenSilently } = useAuth();
  return async ({ queryKey }) => {
    const [, { questionId, filters, surveyId }] = queryKey;
    const token = await getAccessTokenSilently();
    const response = await fetch(
      `${API_BASE_URL}api/v1/questions/${questionId}/raw/${
        filters ? `?${new URLSearchParams(filters).toString()}` : ''
      }`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          [SURVEY_ID_HEADER]: surveyId,
        },
      },
    );
    if (!response.ok) {
      throw new Error('Something went wrong');
    }
    return { body: await response.blob(), headers: response.headers };
  };
}

export function useAggregateResponses({ questionId, surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.AGGREGATE_RESPONSES, { questionId, surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/questions/${questionId}/responses/aggregate/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useQuestionResponses({ questionId, filters, surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.QUESTION_RESPONSES, { questionId, filters, surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/questions/${questionId}/responses/`,
        filters,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useResponseOptions({ questionId, surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.RESPONSE_OPTIONS, { questionId, surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/questions/${questionId}/response-options/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useMultipleResponseOptions(queries = []) {
  const fetcher = useFetch();
  return useQueries({
    queries: queries.map(({ questionId, surveyId, ...queryOptions }) => ({
      queryKey: [QueryKeys.RESPONSE_OPTIONS, { questionId, surveyId }],
      queryFn: () =>
        fetcher({
          path: `v1/questions/${questionId}/response-options/`,
          headers: { [SURVEY_ID_HEADER]: surveyId },
        }),
      ...queryOptions,
    })),
  });
}

export function useQuestion({ questionId, surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.QUESTION, { questionId, surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/questions/${questionId}/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useSuggestedGroups({ questionId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SUGGESTED_GROUPS, { questionId }],
    queryFn: () =>
      fetcher({
        path: `v1/questions/${questionId}/suggested-groups/`,
      }),
    ...options,
  });
}

export function useUpdateQuestionPhase() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, surveyId, data }) => {
      if (!surveyId) {
        throw new MissingIdError('surveyId');
      }
      return fetcher({
        path: `v1/questions/${questionId}/update-phase/`,
        method: 'PATCH',
        data: JSON.stringify(data),
      });
    },
    onSettled: (_data, _error, { questionId, surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.QUESTIONS, { surveyId }]);
      queryClient.invalidateQueries([QueryKeys.QUESTION, { questionId, surveyId }]);
    },
    onError: () => {
      message.error('Something went wrong updating question phase');
    },
  });
}

export function useRemapQuestion() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, surveyId, data }) => {
      if (!surveyId) {
        throw new MissingIdError('surveyId');
      }
      return fetcher({
        path: `v1/questions/${questionId}/remap/`,
        method: 'PUT',
        data: JSON.stringify(data),
      });
    },
    onSettled: (_data, _error, { questionId, surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.QUESTIONS, { surveyId }]);
      queryClient.invalidateQueries([QueryKeys.QUESTION, { questionId, surveyId }]);
    },
    onError: () => {
      message.error('Something went wrong remapping themes');
    },
  });
}

export function useSentiments({ questionId, surveyId }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.SENTIMENTS, { questionId, surveyId }],
    queryFn: () =>
      fetcher({
        path: `v1/questions/${questionId}/sentiments/`,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useCodeFrame({ questionId, filters, surveyId, hiddenThemes }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.CODE_FRAME, { questionId, filters, surveyId, hiddenThemes }],
    queryFn: () =>
      fetcher({
        path: `v1/questions/${questionId}/codeframe/`,
        filters,
        headers: { [SURVEY_ID_HEADER]: surveyId },
      }),
    ...options,
  });
}

export function useCodeFrames(queries = [], options = {}) {
  const fetcher = useFetch();
  return useQueries({
    queries: queries.map(({ questionId, filters, surveyId, hiddenThemes, ...queryOptions }) => ({
      queryKey: [QueryKeys.CODE_FRAME, { questionId, filters, surveyId, hiddenThemes }],
      queryFn: () =>
        fetcher({
          path: `v1/questions/${questionId}/codeframe/`,
          filters,
          headers: { [SURVEY_ID_HEADER]: surveyId },
        }),
      ...queryOptions,
      ...options,
    })),
  });
}

export function useUpdateQuestion() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, surveyId, data }) => {
      if (!surveyId) {
        throw new MissingIdError('surveyId');
      }
      return fetcher({
        path: `v1/questions/${questionId}/update/`,
        method: 'PATCH',
        data: JSON.stringify(data),
      });
    },
    onSettled: (_data, _error, { questionId, surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.QUESTIONS, { surveyId }]);
      queryClient.invalidateQueries([QueryKeys.QUESTION, { questionId, surveyId }]);
    },
    onError: () => {
      message.error('Something went wrong updating question');
    },
  });
}

export function useCreateCodeFrame() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, surveyId }) => {
      if (!surveyId) {
        throw new MissingIdError('surveyId');
      }
      return fetcher({
        path: `v1/questions/${questionId}/codeframe/create/`,
        method: 'POST',
      });
    },
    onSettled: (_data, _error, { questionId, surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.QUESTIONS, { surveyId }]);
      queryClient.invalidateQueries([QueryKeys.QUESTION, { questionId, surveyId }]);
      queryClient.invalidateQueries([QueryKeys.CODE_FRAME, { questionId, surveyId }]);
    },
    onError: () => {
      message.error('Something went wrong creating code frame');
    },
  });
}

export function useLanguages() {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.LANGUAGES],
    queryFn: () =>
      fetcher({
        path: `v1/questions/languages/`,
      }),
  });
}

export function useDetectLanguages() {
  const fetcher = useFetch();
  return useMutation({
    mutationFn: ({ questions }) =>
      fetcher({
        path: 'v1/questions/detect-languages/',
        method: 'POST',
        data: JSON.stringify({ questions }),
        headers: { 'Content-Type': 'application/json' },
        timeoutMs: 30_000,
      }),
    onError: (error) => {
      if (error?.message === 'Request timed out') {
        message.error('Language detection timed out. Please try again.');
        return;
      }
      message.error('Something went wrong detecting languages');
    },
  });
}

export function useImportCodeFrame() {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, codeFrameId, surveyId }) => {
      if (!surveyId) {
        throw new MissingIdError('surveyId');
      }
      return fetcher({
        path: `v1/questions/${questionId}/codeframe/${codeFrameId}/import/`,
        method: 'POST',
      });
    },
    onSettled: (_data, _error, { questionId, surveyId }) => {
      queryClient.invalidateQueries([QueryKeys.QUESTIONS, { surveyId }]);
      queryClient.invalidateQueries([QueryKeys.QUESTION, { questionId, surveyId }]);
    },
    onError: () => {
      message.error('Something went wrong importing code frame');
    },
  });
}
